import { useEffect, useRef, useState } from "react";
import type { PageFilter } from "../lib/pageFilters.js";

export function PageFilterDropdown(props: {
  filter: PageFilter;
  options: string[];
  selected: Set<string>;
  onToggle: (value: string) => void;
  onSelectAll: () => void;
  onClear: () => void;
  /**
   * Set when the viewer may not read this slicer's source: the label stays,
   * the control is disabled, and no options are shown (they are that
   * source's data). The hub's sentence rides as the tooltip.
   */
  unavailableMessage?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { filter, options, selected, onToggle, onSelectAll, onClear, unavailableMessage } = props;
  const unavailable = unavailableMessage !== undefined;

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const label = filter.label || "Filter";
  const summary = unavailable
    ? "Not available to you"
    : selected.size === 0
    ? "All"
    : selected.size === 1
      ? Array.from(selected)[0]
      : `${selected.size} selected`;

  return (
    <div className="pageFilterDropdown" ref={ref}>
      <button
        className="pageFilterTrigger"
        type="button"
        disabled={unavailable}
        title={unavailable ? unavailableMessage : undefined}
        onClick={() => setOpen(!open)}
      >
        <span className="pageFilterLabel">{label}</span>
        <span className="pageFilterValue">{summary}</span>
        <span className="pageFilterArrow">{open ? "▲" : "▼"}</span>
      </button>
      {open && !unavailable ? (
        <div className="pageFilterPanel">
          <div className="pageFilterActions">
            <button className="linkBtn" type="button" onClick={onSelectAll}>Select all</button>
            <button className="linkBtn" type="button" onClick={onClear}>Clear</button>
          </div>
          <div className="pageFilterOptions">
            {options.length === 0 ? (
              <div className="muted" style={{ padding: "8px 12px", fontSize: 13 }}>No values available</div>
            ) : null}
            {options.map((opt) => (
              <label key={opt} className="pageFilterOption">
                <input
                  type="checkbox"
                  checked={selected.has(opt)}
                  onChange={() => onToggle(opt)}
                />
                <span>{opt}</span>
              </label>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
